import { useEffect, useMemo, useState } from "react";
import { useSlideStore } from "@/features/tutor/store/useSlideStore";
import { useSessionStore } from "@/features/tutor/store/useSessionStore";
import { useTurnStore } from "@/features/tutor/store/useTurnStore";
import { useWebsocketStore } from "@/features/tutor/store/useWebsocketStore";
import { useStudentInputStore } from "@/features/tutor/store/useStudentInputStore";
import { useResumeDebugStore } from "@/store/useResumeDebugStore";

interface ResumeDebugPanelProps {
  pendingRehydratePayload?: unknown;
}

function formatTs(ts: number): string {
  return new Date(ts).toLocaleTimeString();
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function parseActionsArray(slideData: unknown): unknown[] {
  try {
    const parsed =
      typeof slideData === "string" ? JSON.parse(slideData) : slideData;
    return Array.isArray(parsed?.actions) ? parsed.actions : [];
  } catch {
    return [];
  }
}

const SHOW_RESUME_DEBUG_PANEL =
  import.meta.env.VITE_SHOW_RESUME_DEBUG_PANEL === "true";

const ResumeDebugPanelContent = ({
  pendingRehydratePayload = null,
}: ResumeDebugPanelProps) => {
  const [collapsed, setCollapsed] = useState(false);
  const [copyState, setCopyState] = useState<"idle" | "success" | "error">(
    "idle",
  );
  const [deckState, setDeckState] = useState<{
    h?: number;
    v?: number;
    f?: number;
    hasDeck: boolean;
  }>({ hasDeck: false });

  const sessionId = useSessionStore((state) => state.sessionId);
  const currentScreen = useSessionStore((state) => state.currentScreen);
  const turnId = useTurnStore((state) => state.turnId);
  const wsConnected = useWebsocketStore((state) => state.wsConnected);
  const currentSlideIndex = useSlideStore((state) => state.currentSlideIndex);
  const slides = useSlideStore((state) => state.slides);
  const slideActionCount = useSlideStore((state) => state.slideActionCount);
  const inputKeyToActionId = useStudentInputStore(
    (state) => state.inputKeyToActionId,
  );
  const inputs = useStudentInputStore((state) => state.inputs);
  const revealedInputs = useStudentInputStore((state) => state.revealedInputs);
  const processingInputs = useStudentInputStore(
    (state) => state.processingInputs,
  );
  const { events, clearEvents, latestSttOutput, latestTtsOutput } =
    useResumeDebugStore();
  const perfEvents = useMemo(
    () => events.filter((event) => event.type.startsWith("perf:")),
    [events],
  );
  const perfSummary = useMemo(
    () =>
      perfEvents
        .map((event) => {
          const data =
            event.data && typeof event.data === "object"
              ? (event.data as Record<string, unknown>)
              : {};
          return {
            event: event.type.replace(/^perf:/, ""),
            durationMs:
              typeof data.duration_ms === "number" ? data.duration_ms : null,
            variant:
              typeof data.variant === "string" ? data.variant : undefined,
            slideIndex:
              typeof data.slide_index === "number"
                ? data.slide_index
                : undefined,
          };
        })
        .filter((event) => event.durationMs !== null)
        .slice(0, 12),
    [perfEvents],
  );

  const currentSlide = useMemo(
    () =>
      slides.find((slide) => slide?.slide_index === currentSlideIndex) ?? null,
    [currentSlideIndex, slides],
  );
  const actionsArray = useMemo(
    () => parseActionsArray(currentSlide?.slide_data),
    [currentSlide?.slide_data],
  );

  useEffect(() => {
    const timer = setInterval(() => {
      const deck = useSlideStore.getState().currentDeck;
      if (!deck || typeof deck.getIndices !== "function") {
        setDeckState({ hasDeck: false });
        return;
      }

      try {
        const indices = deck.getIndices();
        setDeckState({
          hasDeck: true,
          h: indices?.h,
          v: indices?.v,
          f: indices?.f,
        });
      } catch {
        setDeckState({ hasDeck: true });
      }
    }, 250);

    return () => clearInterval(timer);
  }, []);

  const snapshot = useMemo(
    () => ({
      sessionId,
      turnId,
      currentScreen,
      wsConnected,
      currentSlideIndex,
      slideActionCount,
      deckState,
      pendingRehydratePayload,
      latestSttOutput,
      latestTtsOutput,
      actionsArray,
      inputsCount: Object.keys(inputs || {}).length,
      inputMappingCount: Object.keys(inputKeyToActionId || {}).length,
      revealedInputsCount: Array.from(revealedInputs || []).length,
      processingInputsCount: Array.from(processingInputs || []).length,
      perfEventCount: perfEvents.length,
    }),
    [
      currentScreen,
      currentSlideIndex,
      deckState,
      inputKeyToActionId,
      inputs,
      latestSttOutput,
      latestTtsOutput,
      pendingRehydratePayload,
      processingInputs,
      revealedInputs,
      sessionId,
      slideActionCount,
      actionsArray,
      turnId,
      wsConnected,
      perfEvents.length,
    ],
  );

  const buildDebugDump = (): string => {
    const lines: string[] = [];
    lines.push("=== Resume Debug Dump ===");
    lines.push(`generated_at: ${new Date().toISOString()}`);
    lines.push("");
    lines.push("--- Snapshot ---");
    lines.push(safeStringify(snapshot));
    lines.push("");
    lines.push(`--- Events (${events.length}) ---`);

    events.forEach((event, index) => {
      lines.push(
        `[${index + 1}] ${new Date(event.ts).toISOString()} ${event.type}`,
      );
      if (event.data !== undefined) {
        lines.push(safeStringify(event.data));
      }
      lines.push("");
    });

    return lines.join("\n");
  };

  const copyDebugDump = async () => {
    const dump = buildDebugDump();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(dump);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = dump;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopyState("success");
      window.setTimeout(() => setCopyState("idle"), 1500);
    } catch {
      setCopyState("error");
      window.setTimeout(() => setCopyState("idle"), 2000);
    }
  };

  return (
    <div className="fixed right-3 bottom-3 z-[9999] w-[460px] max-w-[95vw] rounded-md border border-[#2d2d2d] bg-black/90 text-[#d6ffd8] text-xs shadow-lg">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#2d2d2d]">
        <div className="font-mono font-bold">Resume Debug Panel (temp)</div>
        <div className="flex items-center gap-2">
          <button
            className="px-2 py-1 rounded border border-[#3d3d3d] text-[#c8c8c8] hover:bg-[#1a1a1a]"
            onClick={copyDebugDump}
          >
            {copyState === "success"
              ? "Copied"
              : copyState === "error"
                ? "Copy failed"
                : "Copy"}
          </button>
          <button
            className="px-2 py-1 rounded border border-[#3d3d3d] text-[#c8c8c8] hover:bg-[#1a1a1a]"
            onClick={clearEvents}
          >
            Clear
          </button>
          <button
            className="px-2 py-1 rounded border border-[#3d3d3d] text-[#c8c8c8] hover:bg-[#1a1a1a]"
            onClick={() => setCollapsed((prev) => !prev)}
          >
            {collapsed ? "Expand" : "Collapse"}
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="max-h-[70vh] overflow-auto p-3 space-y-3">
          <div>
            <div className="font-semibold text-[#99e8ff] mb-1">
              Live Snapshot
            </div>
            <pre className="whitespace-pre-wrap break-all text-[11px] leading-4 bg-[#101010] p-2 rounded">
              {safeStringify(snapshot)}
            </pre>
          </div>

          <div>
            <div className="font-semibold text-[#99e8ff] mb-1">
              Perf Summary ({perfEvents.length})
            </div>
            <pre className="whitespace-pre-wrap break-all text-[11px] leading-4 bg-[#101010] p-2 rounded min-h-[40px]">
              {perfSummary.length > 0
                ? safeStringify(perfSummary)
                : "No timed perf events yet."}
            </pre>
          </div>

          <div>
            <div className="font-semibold text-[#99e8ff] mb-1">STT Output</div>
            <pre className="whitespace-pre-wrap break-all text-[11px] leading-4 bg-[#101010] p-2 rounded min-h-[40px]">
              {latestSttOutput ?? "No STT output yet."}
            </pre>
          </div>

          <div>
            <div className="font-semibold text-[#99e8ff] mb-1">TTS Output</div>
            <pre className="whitespace-pre-wrap break-all text-[11px] leading-4 bg-[#101010] p-2 rounded min-h-[40px]">
              {latestTtsOutput ?? "No TTS output yet."}
            </pre>
          </div>

          <div>
            <div className="font-semibold text-[#99e8ff] mb-1">
              Actions Array ({actionsArray.length})
            </div>
            <pre className="whitespace-pre-wrap break-all text-[11px] leading-4 bg-[#101010] p-2 rounded">
              {safeStringify(actionsArray)}
            </pre>
          </div>

          <div>
            <div className="font-semibold text-[#99e8ff] mb-1">
              Event Timeline ({events.length})
            </div>
            <div className="space-y-2">
              {events.length === 0 && (
                <div className="text-[#bcbcbc]">No events yet.</div>
              )}
              {events.map((event) => (
                <div key={event.id} className="bg-[#101010] p-2 rounded">
                  <div className="text-[#8cf7a6] font-mono">
                    [{formatTs(event.ts)}] {event.type}
                  </div>
                  {event.data !== undefined && (
                    <pre className="mt-1 whitespace-pre-wrap break-all text-[11px] leading-4 text-[#d0d0d0]">
                      {safeStringify(event.data)}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ResumeDebugPanel = (props: ResumeDebugPanelProps) => {
  if (!SHOW_RESUME_DEBUG_PANEL) {
    return null;
  }
  return <ResumeDebugPanelContent {...props} />;
};

export default ResumeDebugPanel;
